import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Card from '../components/ui/Card';
import { useAuth } from '../hooks/useAuth';
import { bookingApi, BookingHostMessagesResponse } from '../services/bookingApi';
import { measureFrameContentHeight } from '../utils/hostMessages';

/**
 * On-site view of the host-published content (event detail + reminder emails)
 * for one booking. Renders the exact email template the guest received so the
 * page never drifts from the email layout (docs/prd-activity-detail-onsite.md).
 */
export default function BookingHostMessages() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['dashboard']);
  const { loading: authLoading, isAuthenticated, getToken } = useAuth();

  const [data, setData] = useState<BookingHostMessagesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [frameHeight, setFrameHeight] = useState(600);
  const frameRef = useRef<HTMLIFrameElement>(null);

  const measureFrame = useCallback(() => {
    if (!frameRef.current) return;
    const height = measureFrameContentHeight(frameRef.current);
    if (height !== null) setFrameHeight(height);
  }, []);

  // Re-measure on viewport changes: the fluid email card reflows on resize
  // and rotation, which changes the content height without another load.
  useEffect(() => {
    window.addEventListener('resize', measureFrame);
    return () => window.removeEventListener('resize', measureFrame);
  }, [measureFrame]);

  const fetchMessages = useCallback(async () => {
    if (!bookingId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(false);
      const token = await getToken();
      if (!token) throw new Error('No access token available');
      const result = await bookingApi.getHostMessages(bookingId, token, i18n.language);
      setData(result);
    } catch (err) {
      if (err instanceof Error && err.message === 'NOT_FOUND') {
        setNotFound(true);
      } else {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [bookingId, getToken, i18n.language]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchMessages();
  }, [authLoading, isAuthenticated, navigate, fetchMessages]);

  if (authLoading || loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3" />
          <div className="h-32 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (notFound || error || !data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-600 mb-6">
          {error ? t('dashboard:hostMessages.loadError') : t('dashboard:hostMessages.notFound')}
        </p>
        <Link to="/dashboard?tab=bookings" className="text-primary hover:text-primary-dark">
          {t('dashboard:hostMessages.backToBookings')}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <Link
        to="/dashboard?tab=bookings"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        {t('dashboard:hostMessages.backToBookings')}
      </Link>

      {data.html ? (
        /* No allow-scripts: the email HTML can never execute code here.
           allow-same-origin only lets the parent measure the frame height;
           allow-popups + <base target="_blank"> open all links in a new tab. */
        <iframe
          ref={frameRef}
          title={t('dashboard:hostMessages.pageTitle')}
          srcDoc={data.html}
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          className="w-full border-0"
          style={{ height: frameHeight }}
          onLoad={measureFrame}
        />
      ) : (
        <Card className="text-center text-gray-500">{t('dashboard:hostMessages.empty')}</Card>
      )}
    </div>
  );
}
